'use client'

import { Controller } from 'react-hook-form'
import clsx from 'clsx'
import { useSession } from 'next-auth/react'

import {
  CustomerTypes,
  extractCustomerFromClientSession,
  queryFetchDeliveryAddresses
} from 'modules/customer'
import {
  gtmNewsletterSignupEvent,
  useNewsletterSignupForm
} from 'modules/newsletter'
import Button from 'shared/ui/Button/Button'
import { ButtonVariants } from 'shared/ui/Button/Button.enums'
import Checkbox from 'shared/ui/form/Checkbox'
import TextField, { TextFieldProps } from 'shared/ui/form/TextField'
import { noop } from 'shared/utils/common'
import { PATHS } from 'shared/utils/paths'

import { pagebuilderNewsletterFormSectionDefaultSettings } from '../../config'
import { PagebuilderNewsletterFormSectionViewModel } from '../../types'
import { mergeSettingsWithDefaults } from '../../utils'
import SectionWrapper from '../SectionWrapper'
import TextGroup from '../TextGroup'

import styles from './PagebuilderNewsletterFormSection.module.scss'

interface PagebuilderNewsletterFormSectionProps {
  section: PagebuilderNewsletterFormSectionViewModel
}

const PagebuilderNewsletterFormSection = ({
  section
}: PagebuilderNewsletterFormSectionProps) => {
  const { id, settings, heading, description } = section
  const session = useSession()
  const customer: CustomerTypes | undefined =
    extractCustomerFromClientSession(session)
  const settingsWithDefaults = mergeSettingsWithDefaults(
    settings,
    pagebuilderNewsletterFormSectionDefaultSettings
  )
  const {
    methods: {
      register,
      handleSubmit,
      control,
      formState: { errors }
    },
    onSubmit,
    isLoading,
    isSuccess
  } = useNewsletterSignupForm({
    onSignedUp: email => {
      void trackNewsletterSignup(email)
    }
  })

  async function trackNewsletterSignup(email: string) {
    if (!customer) {
      gtmNewsletterSignupEvent(email)

      return
    }

    const token = session.data?.user?.token || ''
    const addresses = await queryFetchDeliveryAddresses(token).catch(noop)
    const mainAddress = addresses?.find(address => address.isDefault)

    gtmNewsletterSignupEvent(email, customer, mainAddress)
  }

  return (
    <SectionWrapper id={id || ''} settings={settingsWithDefaults}>
      <div className={styles.contentWrapper}>
        <TextGroup textGroup={{ heading, description }} />
        <form noValidate className={styles.form} onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.emailWrapper}>
            <EmailField
              marginBottom={false}
              placeholder='Wpisz swój email'
              inputProps={{ autoComplete: 'email' }}
              error={!!errors.email}
              helperText={errors.email?.message}
              {...register('email')}
            />
            <Button
              className={styles.submitButton}
              variant={ButtonVariants.secondary}
              size='large'
              type='submit'
              disabled={isLoading || isSuccess}
            >
              Subskrybuj
            </Button>
          </div>
          <div className={styles.checkboxWrapper}>
            <Controller
              name='newsletterAgreement'
              control={control}
              render={({ field }) => (
                <Checkbox
                  ref={field.ref}
                  label={
                    <div className={styles.newsletterConsentWrapper}>
                      Wyrażam zgodę na otrzymywanie treści marketingowych i
                      ofert handlowych od Mamyito Sp. z o.o. z siedzibą we
                      Władysławowie. Twoje dane osobowe będą przetwarzane
                      zgodnie z przepisami
                      <a href={PATHS.privacy} target='_blank' rel='noreferrer'>
                        Polityki Prywatności
                      </a>
                    </div>
                  }
                  name={field.name}
                  value={field.value}
                  onChange={(_, value) => field.onChange(value)}
                  onBlur={field.onBlur}
                  invalid={errors.newsletterAgreement?.message}
                />
              )}
            />
          </div>
        </form>
      </div>
    </SectionWrapper>
  )
}

export default PagebuilderNewsletterFormSection

export const EmailField = ({ className, ...props }: TextFieldProps) => (
  <TextField className={clsx(styles.emailField, className)} {...props} />
)
